import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    forwardRef,
    Input,
    OnDestroy,
    Output
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Store } from '@ngxs/store';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { LocationSublocationDirective } from '@Shared/Directives/proprietary/location-sublocation.directive';
import { ContractObjectService } from '@Core/models/proprietary/contract/contract-object.service';
import { SubLocationState } from '@Core/models/proprietary/contract/sub-location.state';

@Component({
    standalone: false,
    selector: 'ep-sub-location-selector',
    templateUrl: './sub-location-selector.component.html',
    changeDetection: ChangeDetectionStrategy.Default,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SubLocationSelectorComponent),
            multi: true
        }
    ]
})
export class SubLocationSelectorComponent implements ControlValueAccessor, OnDestroy {
    @Output()
    blur = new EventEmitter();

    @Output()
    focus = new EventEmitter();

    @Input('disabled')
    controlDisabled: boolean = false;

    @Input('userDefinedMode')
    set userDefinedMode(value: boolean) {
        if (value === true) {
            this._userDefinedMode = true;
        }
    }

    get userDefinedMode(): boolean {
        return this._userDefinedMode;
    }

    private _userDefinedMode: boolean = false;

    subLocationCodeTable$: Observable<any[]>;
    subLocation: any = {};
    subLocationSub: Subscription;
    locIdSub: Subscription;
    locId: any;

    innerValue: any = '';

    constructor(
        private store: Store,
        private cdRef: ChangeDetectorRef,
        private contractOS: ContractObjectService,
        public locationSublocationDirective: LocationSublocationDirective
    ) {
        this.locIdSub = this.locationSublocationDirective.locId$.subscribe((locId) => {
            this.locId = locId;
            this.getCodeTable();
        });
    }

    //Placeholders for the callbacks which are later provided by the Control Value Accessor
    private onChangeCallback = (_: any) => { };
    private onTouchedCallback = () => { };

    //get accessor
    get value(): any {
        return this.innerValue;
    }

    //set accessor including call the onchange callback
    set value(v: any) {
        if (v !== this.innerValue) {
            this.innerValue = v;
            this.onChangeCallback(v);
            this.onTouchedCallback();
        }
    }

    onFocus() {
        this.focus.emit();
    }

    onBlur() {
        this.blur.emit();
        this.onTouchedCallback();
    }

    writeValue(value: string): void {
        this.innerValue = value;
        this.cdRef.detectChanges();
    }

    //From ControlValueAccessor interface - Allows Angular to register a function to call when the model changes.
    registerOnChange(fn: any) {
        this.onChangeCallback = fn;
    }

    //From ControlValueAccessor interface -  Allows Angular to register a function to call when the input has been touched.
    registerOnTouched(fn: any) {
        this.onTouchedCallback = fn;
    }

    //From ControlValueAccessor interface -  Allows Angular to disable the input.
    setDisabledState(isDisabled: boolean): void {
        this.controlDisabled = isDisabled;
    }

    getCodeTable() {
        if (this.userDefinedMode) {
            this.subLocationCodeTable$ = this.store
                .select(SubLocationState.subLocationIDCodes)
                .pipe(map((fn) => fn(this.locId)));
        } else {
            this.subLocationCodeTable$ = this.store
                .select(SubLocationState.subLocationCodes)
                .pipe(map((fn) => fn(this.locId)));
        }
    }

    ngOnDestroy() {
        if (this.subLocationSub) {
            this.subLocationSub.unsubscribe();
        }
    }

    modalEdit() {
        if (!this.userDefinedMode) {
            this.contractOS.modalSubLocationEdit({ Loc_ID: this.locId, Sub_Loc_Num: this.value });
            if (this.subLocationSub) {
                this.subLocationSub.unsubscribe();
            }
            //when sub location is added it will become the current sub location, 
            // if deleted the current sub loc is cleared (trigger the popup to close)
            this.subLocationSub = this.store.select(SubLocationState.currentSubLocation).subscribe((currentLocation) => {
                if (currentLocation) {
                    this.value = currentLocation.Sub_Loc_Num;
                    if (!currentLocation.Sub_Loc_ID) {
                        this.subLocationSub.unsubscribe();
                    }
                }
            });
        }
    }
}
